import React, {useMemo, useState} from "react";
import Icon from "@mdi/react";
import {mdiChevronDown, mdiChevronRight, mdiLock} from "@mdi/js";
import {Badge, Button, Form} from "react-bootstrap";
import HelpTooltip from "./HelpTooltip.jsx";

/**
 * Gemeinsame Rechte-Matrix für die Rollen-Seite und den Benutzer-Dialog -
 * beide zeigen Rechte genau gleich: eine Karte je Bereich (Präfix vor dem
 * Punkt, Reihenfolge und Beschriftung aus config/permission_areas.php), oben
 * ein Gruppenschalter mit drei Zuständen (aus / teilweise / an), darunter
 * die Einzelschalter. "Gruppe an, dann gezielt einen aus" geht ohne Umweg.
 *
 * Die Matrix hält keinen eigenen Rechtezustand: Sie bekommt den Entwurf
 * (`selected`) und meldet Änderungen über `onChange`. Gespeichert wird
 * gesammelt über die Speicherleiste (`PermissionSaveBar`), nicht je Schalter.
 */

const humanize = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * Entwurf der angehakten Rechte gegenüber dem gespeicherten Stand.
 * `base` ist der gespeicherte Stand (Liste von Rechtenamen).
 */
export function usePermissionDraft(base) {
    const baseKey = JSON.stringify([...base].sort());
    const [draft, setDraft] = useState(base);

    // Neuer gespeicherter Stand (nach dem Speichern oder beim Wechsel der
    // Rolle bzw. des Benutzers) ersetzt den Entwurf noch im selben Zeichnen,
    // damit die Matrix nie kurz den Entwurf des vorigen Objekts zeigt.
    const [seenKey, setSeenKey] = useState(baseKey);
    if (seenKey !== baseKey) {
        setSeenKey(baseKey);
        setDraft(JSON.parse(baseKey));
    }

    const baseSet = useMemo(() => new Set(JSON.parse(baseKey)), [baseKey]);
    const draftSet = useMemo(() => new Set(draft), [draft]);
    const grant = useMemo(() => draft.filter(name => !baseSet.has(name)), [draft, baseSet]);
    const revoke = useMemo(() => [...baseSet].filter(name => !draftSet.has(name)), [baseSet, draftSet]);

    return {
        draft,
        setDraft,
        grant,
        revoke,
        count: grant.length + revoke.length,
        reset: () => setDraft(JSON.parse(baseKey)),
    };
}

/**
 * Titel ohne doppelten Bereichspräfix ("Cosmos: Artikel-Blatt" in der Gruppe
 * "Lieferant Cosmos" -> "Artikel-Blatt"). Entfernt wird der Teil vor ": ",
 * wenn er dem Bereichsnamen entspricht oder von mindestens zwei Rechten der
 * Gruppe geteilt wird.
 */
function shortTitles(items, groupLabel) {
    const prefixOf = permission => {
        const title = permission.title || "";
        const at = title.indexOf(": ");
        return at > 0 ? title.slice(0, at) : null;
    };
    const counts = {};
    items.forEach(permission => {
        const prefix = prefixOf(permission);
        if (prefix) counts[prefix] = (counts[prefix] || 0) + 1;
    });

    return Object.fromEntries(items.map(permission => {
        const prefix = prefixOf(permission);
        const strip = prefix && (counts[prefix] >= 2 || prefix.toLocaleLowerCase("de") === groupLabel.toLocaleLowerCase("de"));
        const title = permission.title
            ? (strip ? permission.title.slice(prefix.length + 2) : permission.title)
            : permission.name;
        return [permission.name, title];
    }));
}

function PmSwitch({state, disabled, onClick, label, className = "", ...rest}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={state === "partial" ? "mixed" : state === "on" ? "true" : "false"}
            aria-label={label}
            disabled={disabled}
            className={`pm-switch ${className}`}
            onClick={onClick}
            {...rest}
        />
    );
}

export default function PermissionMatrix({
    permissions = [],
    areas = [],
    selected = [],
    onChange,
    lockedBy = {},
    readOnly = false,
    readOnlyHint = null,
    idPrefix = "pm",
}) {
    const [query, setQuery] = useState("");
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const isOn = permission => selectedSet.has(permission.name) || Boolean(lockedBy[permission.name]);
    const isLocked = permission => Boolean(lockedBy[permission.name]);

    const groups = useMemo(() => {
        const known = new Map(areas.filter(area => area.prefix !== "").map(area => [area.prefix, []]));
        const fallback = [];
        permissions.forEach(permission => {
            const prefix = permission.name.split(".")[0];
            (known.get(prefix) || fallback).push(permission);
        });
        const result = areas
            .filter(area => area.prefix !== "")
            .map(area => ({prefix: area.prefix, label: area.label, items: known.get(area.prefix)}));
        const fallbackArea = areas.find(area => area.prefix === "");
        result.push({prefix: "", label: fallbackArea?.label || "Weitere Bereiche", items: fallback});
        return result.filter(group => group.items.length > 0);
    }, [permissions, areas]);

    // Beim ersten Zeichnen offen: nur Bereiche mit mindestens einem aktiven Recht.
    const [openPrefixes, setOpenPrefixes] = useState(() => new Set(
        groups.filter(group => group.items.some(permission => isOn(permission))).map(group => group.prefix),
    ));
    const toggleOpen = prefix => setOpenPrefixes(current => {
        const next = new Set(current);
        if (next.has(prefix)) next.delete(prefix); else next.add(prefix);
        return next;
    });

    const needle = query.trim().toLocaleLowerCase("de");
    const searching = needle !== "";

    const setPermissions = (names, on) => {
        const next = new Set(selected);
        names.forEach(name => (on ? next.add(name) : next.delete(name)));
        onChange([...next]);
    };

    const visibleGroups = groups
        .map(group => {
            const titles = shortTitles(group.items, group.label);
            const items = searching
                ? group.items.filter(permission => (
                    `${titles[permission.name]} ${permission.title || ""} ${permission.name}`.toLocaleLowerCase("de").includes(needle)
                ))
                : group.items;
            return {...group, titles, shown: items};
        })
        .filter(group => group.shown.length > 0);

    return (
        <div className="permission-matrix" data-testid="permission-matrix">
            <div className="d-flex align-items-center gap-2 mb-3">
                <Form.Control
                    type="text"
                    size="sm"
                    className="permission-matrix__search"
                    style={{maxWidth: 360}}
                    placeholder="Rechte suchen (Titel oder Name)"
                    aria-label="Rechte suchen"
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                />
                <HelpTooltip id={`${idPrefix}-suche`} ariaLabel="Hilfe zur Suche" placement="bottom">
                    Filtert die Rechte nach Titel oder technischem Namen. Gruppen ohne Treffer werden
                    ausgeblendet, Gruppen mit Treffer klappen auf. Die Gruppenschalter wirken auch
                    während der Suche auf den ganzen Bereich, nicht nur auf die gefilterten Rechte.
                </HelpTooltip>
            </div>

            {readOnly && readOnlyHint && (
                <div className="alert alert-secondary py-2 px-3 small permission-matrix__hint" role="status">
                    {readOnlyHint}
                </div>
            )}

            {visibleGroups.length === 0 && (
                <p className="text-muted small mb-0">Keine Rechte zum Suchbegriff gefunden.</p>
            )}

            {visibleGroups.map(group => {
                const total = group.items.length;
                const onCount = group.items.filter(permission => isOn(permission)).length;
                const switchable = group.items.filter(permission => !isLocked(permission));
                const state = onCount === 0 ? "off" : onCount === total ? "on" : "partial";
                const open = searching || openPrefixes.has(group.prefix);
                const groupId = `${idPrefix}-group-${group.prefix || "weitere"}`;
                const lockedCount = group.items.filter(permission => isLocked(permission)).length;

                const clickGroup = () => {
                    // aus oder teilweise -> alle an; an -> alle aus. Rechte, die über
                    // eine Rolle gewährt werden, bleiben unberührt.
                    setPermissions(switchable.map(permission => permission.name), state !== "on");
                };

                return (
                    <section
                        key={group.prefix || "weitere"}
                        className={`permission-group${open ? " is-open" : ""}`}
                        data-area={group.prefix || "weitere"}
                    >
                        <div className="permission-group__header">
                            <button
                                type="button"
                                className="permission-group__toggle"
                                aria-expanded={open}
                                aria-controls={`${groupId}-body`}
                                onClick={() => toggleOpen(group.prefix)}
                            >
                                <Icon path={open ? mdiChevronDown : mdiChevronRight} size={0.9}/>
                                <span>{group.label}</span>
                            </button>
                            <Badge bg={state === "off" ? "secondary" : "success"} pill className="permission-group__count">
                                {onCount} von {total}
                            </Badge>
                            <HelpTooltip id={`${groupId}-hilfe`} ariaLabel={`Hilfe zu ${group.label}`} placement="left">
                                Bereich „{group.label}“. Der Gruppenschalter schaltet alle Rechte des Bereichs
                                gemeinsam: bei „aus“ oder „teilweise“ alle an, bei „an“ alle aus. Danach
                                lässt sich gezielt ein einzelnes Recht umstellen.
                                {lockedCount > 0 && " Rechte, die über eine Rolle kommen, bleiben dabei unverändert."}
                            </HelpTooltip>
                            <PmSwitch
                                className="permission-group__switch"
                                state={state}
                                disabled={readOnly || switchable.length === 0}
                                label={`Ganzen Bereich ${group.label} schalten`}
                                onClick={clickGroup}
                                data-state={state}
                                data-area={group.prefix || "weitere"}
                            />
                        </div>

                        {open && (
                            <div className="permission-group__body" id={`${groupId}-body`}>
                                {group.shown.map(permission => {
                                    const locked = isLocked(permission);
                                    const on = isOn(permission);
                                    const roles = lockedBy[permission.name] || [];
                                    const title = group.titles[permission.name];

                                    return (
                                        <div
                                            className="permission-item"
                                            key={permission.name}
                                            data-permission={permission.name}
                                        >
                                            <PmSwitch
                                                className="permission-item__switch"
                                                state={on ? "on" : "off"}
                                                disabled={readOnly || locked}
                                                label={`${title} (${permission.name})`}
                                                onClick={() => setPermissions([permission.name], !on)}
                                                data-permission={permission.name}
                                            />
                                            <div className="permission-item__label">
                                                <span>{title}</span>
                                                <span className="permission-item__name">{permission.name}</span>
                                            </div>
                                            {permission.protected && (
                                                <HelpTooltip
                                                    id={`${idPrefix}-geschuetzt-${permission.name}`}
                                                    iconPath={mdiLock}
                                                    ariaLabel="Geschütztes Recht"
                                                    className="permission-item__lock"
                                                    placement="left"
                                                >
                                                    Geschütztes Recht: nur der Superadmin kann es vergeben, einzeln
                                                    an eine Person oder über eine Rolle. Andere sehen es nicht.
                                                </HelpTooltip>
                                            )}
                                            {locked && roles.includes("@superadmin") && (
                                                <>
                                                    <Badge bg="secondary" className="permission-item__origin">
                                                        über Superadmin
                                                    </Badge>
                                                    <HelpTooltip
                                                        id={`${idPrefix}-superadmin-${permission.name}`}
                                                        ariaLabel="Herkunft des Rechts"
                                                        placement="left"
                                                    >
                                                        Als Superadmin hast du dieses Recht ohnehin. Nur die versteckten
                                                        Bereiche (etwa Cosmos, Dokumente, Vertriebsauswertung,
                                                        Auftrags-Editor) bekommt auch der Superadmin ausschließlich per
                                                        Einzelzuweisung – diese Schalter sind frei.
                                                    </HelpTooltip>
                                                </>
                                            )}
                                            {locked && !roles.includes("@superadmin") && (
                                                <>
                                                    <Badge bg="info" text="dark" className="permission-item__origin">
                                                        über Rolle {roles.join(", ")}
                                                    </Badge>
                                                    <HelpTooltip
                                                        id={`${idPrefix}-rolle-${permission.name}`}
                                                        ariaLabel="Herkunft des Rechts"
                                                        placement="left"
                                                    >
                                                        Dieses Recht kommt über die Rolle {roles.join(", ")} und ist
                                                        deshalb gesperrt. Einzelrechte sind rein additiv (es gibt kein
                                                        Verbieten): Entziehen lässt es sich nur über die Rolle.
                                                    </HelpTooltip>
                                                </>
                                            )}
                                            {!locked && (
                                                <HelpTooltip
                                                    id={`${idPrefix}-hilfe-${permission.name}`}
                                                    ariaLabel={`Hilfe zu ${permission.name}`}
                                                    placement="left"
                                                >
                                                    {permission.title || permission.name} ({permission.name}).
                                                    {" "}Schaltet nur dieses Recht ein oder aus; gespeichert wird erst über die Leiste „Speichern“.
                                                </HelpTooltip>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

/**
 * Speicherleiste unter der Matrix: "3 Änderungen nicht gespeichert —
 * Speichern / Verwerfen". Erscheint nur, wenn es etwas zu speichern gibt.
 */
export function PermissionSaveBar({count, saving = false, error = "", onSave, onDiscard}) {
    if (count === 0 && !error) return null;

    return (
        <div className="permission-savebar" role="status">
            {count > 0 && (
                <span className="permission-savebar__text fw-semibold">
                    {humanize(count, "Änderung", "Änderungen")} nicht gespeichert
                </span>
            )}
            {error && <span className="text-danger small permission-savebar__error">{error}</span>}
            <span className="ms-auto d-inline-flex align-items-center gap-2">
                <HelpTooltip id="pm-savebar-hilfe" ariaLabel="Hilfe zur Speicherleiste" placement="top">
                    Alle Änderungen werden mit einem einzigen Speichern übernommen, die Seite lädt
                    dabei nicht neu. „Verwerfen“ stellt den zuletzt gespeicherten Stand wieder her.
                </HelpTooltip>
                <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="permission-savebar__discard"
                    disabled={saving || count === 0}
                    onClick={onDiscard}
                >
                    Verwerfen
                </Button>
                <Button
                    type="button"
                    size="sm"
                    className="permission-savebar__save"
                    disabled={saving || count === 0}
                    onClick={onSave}
                >
                    {saving ? "Wird gespeichert…" : "Speichern"}
                </Button>
            </span>
        </div>
    );
}
